import type { HttpErrorResponse } from '@angular/common/http';
import { computed, inject } from '@angular/core';
import type { MachineCatalogEntry } from '@indutrust/machine-model';
import {
  resolveHttpError,
  withCrudEntitiesEvents,
  withOrderedEntities,
  withRequestStatus,
} from '@indutrust/shared-util';
import { mapResponse } from '@ngrx/operators';
import { signalStore, withComputed, withState } from '@ngrx/signals';
import {
  Events,
  on,
  withEventHandlers,
  withReducer,
} from '@ngrx/signals/events';
import {
  exhaustMap,
  filter,
  from,
  map,
  switchMap,
  takeWhile,
  timer,
} from 'rxjs';
import { MachineCatalogApiClient } from '../machine-catalog-api-client.service';
import { isMachineExtractionActive } from '../machine-catalog-extraction.util';
import {
  adminMachineCatalogCrudEvents,
  adminMachineCatalogWorkflowEvents,
} from './admin-machine-catalog.events';
import { machineCatalogCrudEvents } from './machine-catalog.events';

const POLL_INTERVAL_MS = 4500;

type AdminMachineCatalogState = {
  polling: boolean;
  workflowBusy: boolean;
};

function anyExtractionActive(entries: MachineCatalogEntry[]): boolean {
  return entries.some(isMachineExtractionActive);
}

/**
 * Admin store for the shared machine catalog — unscoped platform view.
 *
 * Combines Pattern A CRUD ({@link adminMachineCatalogCrudEvents}) with
 * workflow events for approve / reject / datasheet extraction. Polls
 * while any entry has an active extraction job.
 *
 * On `workflowSucceeded`, reloads this admin list. When the entry is
 * `approved`, also reloads the supplier-facing
 * {@link machineCatalogCrudEvents} store so pickers see the new machine.
 * Approve/reject themselves update the originating factory
 * {@link import('@indutrust/factory-model').MachinePark} row on the backend
 * (demo MSW mirrors that via `notifyMachineCatalogDecision`).
 *
 * Loaded on demand from `/backoffice/machines` and
 * `/backoffice/machines/pending` — not auto-loaded on auth.
 *
 * @example
 * const store = inject(AdminMachineCatalogStore);
 * const dispatch = injectDispatch(adminMachineCatalogCrudEvents);
 * dispatch.loadAllRequested({});
 */
export const AdminMachineCatalogStore = signalStore(
  { providedIn: 'root' },
  withCrudEntitiesEvents<MachineCatalogEntry>(
    adminMachineCatalogCrudEvents,
    () => inject(MachineCatalogApiClient)
  ),
  withOrderedEntities<MachineCatalogEntry>(),
  withRequestStatus(),
  withState<AdminMachineCatalogState>({
    polling: false,
    workflowBusy: false,
  }),
  withComputed(({ entities }) => ({
    approvedEntries: computed(() =>
      entities().filter((entry) => entry.status === 'approved')
    ),
    pendingEntries: computed(() =>
      entities().filter((entry) => entry.status === 'pending')
    ),
    rejectedEntries: computed(() =>
      entities().filter((entry) => entry.status === 'rejected')
    ),
    hasActiveExtraction: computed(() => anyExtractionActive(entities())),
  })),
  withReducer(
    on(adminMachineCatalogWorkflowEvents.approveRequested, () => ({
      workflowBusy: true,
    })),
    on(adminMachineCatalogWorkflowEvents.rejectRequested, () => ({
      workflowBusy: true,
    })),
    on(adminMachineCatalogWorkflowEvents.analyzeDatasheetsRequested, () => ({
      workflowBusy: true,
    })),
    on(adminMachineCatalogWorkflowEvents.applySuggestionsRequested, () => ({
      workflowBusy: true,
    })),
    on(adminMachineCatalogWorkflowEvents.dismissSuggestionsRequested, () => ({
      workflowBusy: true,
    })),
    on(adminMachineCatalogWorkflowEvents.pollRequested, () => ({
      polling: true,
    })),
    on(adminMachineCatalogWorkflowEvents.workflowSucceeded, () => ({
      workflowBusy: false,
    })),
    on(adminMachineCatalogWorkflowEvents.workflowFailed, ({ payload }) => ({
      workflowBusy: false,
      requestStatus: { error: payload.error },
    })),
    on(adminMachineCatalogCrudEvents.loadAllSucceeded, ({ payload }) => ({
      polling: anyExtractionActive(payload),
    }))
  ),
  withEventHandlers(
    (
      store,
      events = inject(Events),
      client = inject(MachineCatalogApiClient)
    ) => {
      const toFailed = (error: HttpErrorResponse) =>
        adminMachineCatalogWorkflowEvents.workflowFailed({
          error: resolveHttpError(error).code,
        });

      return {
        approve$: events
          .on(adminMachineCatalogWorkflowEvents.approveRequested)
          .pipe(
            switchMap(({ payload }) =>
              client.approve(payload.id).pipe(
                mapResponse({
                  next: (entry) =>
                    adminMachineCatalogWorkflowEvents.workflowSucceeded({
                      entry,
                    }),
                  error: toFailed,
                })
              )
            )
          ),

        reject$: events
          .on(adminMachineCatalogWorkflowEvents.rejectRequested)
          .pipe(
            switchMap(({ payload }) =>
              client.reject(payload.id, payload.reason).pipe(
                mapResponse({
                  next: (entry) =>
                    adminMachineCatalogWorkflowEvents.workflowSucceeded({
                      entry,
                    }),
                  error: toFailed,
                })
              )
            )
          ),

        analyze$: events
          .on(adminMachineCatalogWorkflowEvents.analyzeDatasheetsRequested)
          .pipe(
            switchMap(({ payload }) =>
              client.analyzeDatasheets(payload.id).pipe(
                mapResponse({
                  next: (entry) =>
                    adminMachineCatalogWorkflowEvents.workflowSucceeded({
                      entry,
                    }),
                  error: toFailed,
                })
              )
            )
          ),

        applySuggestions$: events
          .on(adminMachineCatalogWorkflowEvents.applySuggestionsRequested)
          .pipe(
            switchMap(({ payload }) =>
              client.applySuggestions(payload.id, payload.payload).pipe(
                mapResponse({
                  next: (entry) =>
                    adminMachineCatalogWorkflowEvents.workflowSucceeded({
                      entry,
                    }),
                  error: toFailed,
                })
              )
            )
          ),

        dismissSuggestions$: events
          .on(adminMachineCatalogWorkflowEvents.dismissSuggestionsRequested)
          .pipe(
            switchMap(({ payload }) =>
              client.dismissSuggestions(payload.id).pipe(
                mapResponse({
                  next: (entry) =>
                    adminMachineCatalogWorkflowEvents.workflowSucceeded({
                      entry,
                    }),
                  error: toFailed,
                })
              )
            )
          ),

        refreshOnWorkflow$: events
          .on(adminMachineCatalogWorkflowEvents.workflowSucceeded)
          .pipe(
            switchMap(({ payload }) => {
              const refreshes = [
                adminMachineCatalogCrudEvents.loadAllRequested({}),
              ];
              if (payload.entry.status === 'approved') {
                refreshes.push(machineCatalogCrudEvents.loadAllRequested({}));
              }
              return from(refreshes);
            })
          ),

        poll$: events.on(adminMachineCatalogWorkflowEvents.pollRequested).pipe(
          exhaustMap(() =>
            timer(POLL_INTERVAL_MS, POLL_INTERVAL_MS).pipe(
              switchMap(() => client.getAll()),
              takeWhile((entries) => anyExtractionActive(entries), true),
              map((entities) =>
                adminMachineCatalogCrudEvents.loadAllSucceeded(entities)
              )
            )
          )
        ),

        startPollOnActiveExtraction$: events
          .on(adminMachineCatalogCrudEvents.loadAllSucceeded)
          .pipe(
            filter(
              ({ payload }) => anyExtractionActive(payload) && !store.polling()
            ),
            map(() => adminMachineCatalogWorkflowEvents.pollRequested())
          ),
      };
    }
  )
);
